// scenarios/ScenarioThemeWrapper.tsx — in-game theme wrapper.
//
// Self-contained theme container for the post-login surface. Unlike
// ScenarioContext (which drives the login pill row's hover/preview/select
// state machine), this is a single dumb wrapper: take a scenarioId, look
// up its presentation, set its theme variables on a wrapping div, render
// children. Pure DOM mutation — no preview state, no providers, no hooks.
//
// All in-game styles in styles.ts read these variables (with Ember Hollow
// fallbacks) so the page recolours when scenarioId changes. When the user
// switches stories via the title dropdown, the new scenario's theme
// applies the moment currentStory.scenarioId updates.
//
// Dimension-driven overrides:
// Scenarios may declare `dimensionThemes: Record<number, Partial<ScenarioTheme>>`
// in their presentation. When set, and when the current `state.dimension`
// has an entry, the entry's values are merged onto the base theme before
// the CSS variables are written. Only Veridian Bloom and The Sleeping
// Floor use this today — the simulation thriller and the dream-architecture
// world both want the page to visibly shift as the player descends. Other
// scenarios that use state.level for spatial floors (Vaultsworn, Salt-Glass
// Tower) deliberately keep their theme constant across floors.
//
// `layerThemes` is consulted as a back-compat alias for one release while
// we finish the rename.

import { type CSSProperties, type ReactNode } from "react";
import {
  SCENARIO_PRESENTATIONS,
  type ScenarioTheme,
} from "./presentation";

interface ScenarioThemeWrapperProps {
  /** Active story's scenarioId. May be null briefly during boot. */
  scenarioId: string | null;
  /**
   * Active story's `state.dimension`. Default 0 if the story doesn't use
   * the dimension mechanic. Drives the dimension-theme overlay merge.
   */
  currentDimension?: number;
  children: ReactNode;
}

/**
 * Merge a partial dimension override onto the base theme. Missing keys in
 * the override fall through to the base. Pure; no side effects.
 */
function mergeDimensionTheme(
  base: ScenarioTheme,
  override: Partial<ScenarioTheme> | undefined,
): ScenarioTheme {
  if (!override) return base;
  return { ...base, ...override };
}

/**
 * Wraps in-game UI in a div whose inline style sets the scenario's theme
 * variables (--bg-gradient, --accent, --ink, etc.).
 *
 * scenarioId may be null briefly during boot; in that case we render
 * children with no inline vars set, and the styles.ts fallbacks take over.
 */
export default function ScenarioThemeWrapper({
  scenarioId,
  currentDimension,
  children,
}: ScenarioThemeWrapperProps) {
  const presentation =
    scenarioId !== null ? SCENARIO_PRESENTATIONS[scenarioId] : undefined;

  // No presentation (null id, or an id the web client hasn't been deployed
  // to know about yet) — render bare. styles.ts var() fallbacks paint the
  // default Ember Hollow palette.
  if (!presentation) {
    return <>{children}</>;
  }

  // Pick the dimension overlay if the scenario declares one for the current
  // dimension. Default dimension is 0 — the surface — and most scenarios
  // either declare a dimension-0 override or leave the base theme as-is.
  const dimension =
    typeof currentDimension === "number" && Number.isFinite(currentDimension)
      ? Math.trunc(currentDimension)
      : 0;
  const dimensionOverride =
    presentation.dimensionThemes?.[dimension] ??
    presentation.layerThemes?.[dimension];
  const theme = mergeDimensionTheme(presentation.theme, dimensionOverride);

  // Cast each entry through `as keyof CSSProperties` so TypeScript accepts
  // the CSS-custom-property names. They're not in the CSSProperties type
  // but React passes them through to the DOM untouched.
  const vars: CSSProperties = {
    ["--bg-gradient" as keyof CSSProperties]: theme.bgGradient,
    ["--accent" as keyof CSSProperties]: theme.accent,
    ["--accent-muted" as keyof CSSProperties]: theme.accentMuted,
    ["--ink" as keyof CSSProperties]: theme.ink,
    ["--ink-muted" as keyof CSSProperties]: theme.inkMuted,
    ["--paper" as keyof CSSProperties]: theme.paper,
    ["--paper-edge" as keyof CSSProperties]: theme.paperEdge,
    ["--font-body" as keyof CSSProperties]: theme.fontFamily,
    // Smooth swap as the variables change between themes. Don't transition
    // transform / layout — only colour-bearing properties.
    transition:
      "background 0.4s ease-out, color 0.4s ease-out, border-color 0.4s ease-out",
  } as CSSProperties;

  return <div style={vars}>{children}</div>;
}
